// src/cockpit4/components/KnowledgeQualityPanel.tsx
// ============================================
// P0-功能3：知识质量评估（知识中心）
//
// 功能：
//   1. 质量分布统计（高质量 / 中质量 / 低质量 数量统计）
//   2. 低质量知识列表（优先处理，按质量分升序）
//   3. 质量趋势图（recharts 饼图展示分布）
//   4. 知识贡献者排行榜（基于笔记路径解析作者）
//   5. 质量评分算法（客观指标：长度、结构、链接数、字数；分项评分显示）
//   6. 过期检测（基于创建/更新时间，超过 180 天标记为"可能过时"）
//
// 数据来源：
//   - /vault-api/list 获取笔记列表
//   - /vault-api/read 抽样读取评估质量
//
// 质量评分算法（0-100）：
//   - 长度分（0-30）：基于内容字符数
//   - 结构分（0-25）：基于 Markdown 结构（标题、列表、代码块、表格）
//   - 链接分（0-20）：基于 [[wikilinks]] 和 [text](url) 数量
//   - 字数分（0-25）：基于有效字数（中文 + 英文词）
// ============================================

import { useCallback, useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import {
  BarChart3,
  AlertTriangle,
  Users,
  FileText,
  RefreshCw,
  Loader2,
  X,
  Award,
  Clock,
  Link2,
  ListTree,
  Type,
  ExternalLink,
  ChevronDown,
  ChevronUp,
} from "lucide-react";

// ============================================
// 类型定义
// ============================================

/** 笔记元信息 */
interface NoteMeta {
  name: string;
  path: string;
  mtime: string;
  size: number;
}

/** 质量等级 */
type QualityLevel = "high" | "medium" | "low";

/** 质量评分分项 */
interface QualityBreakdown {
  /** 长度分（0-30） */
  lengthScore: number;
  /** 结构分（0-25） */
  structureScore: number;
  /** 链接分（0-20） */
  linkScore: number;
  /** 字数分（0-25） */
  wordCountScore: number;
  /** 总分（0-100） */
  total: number;
}

/** 质量评估项 */
interface QualityItem {
  note: NoteMeta;
  /** 质量总分 */
  score: number;
  /** 质量等级 */
  level: QualityLevel;
  /** 分项评分 */
  breakdown: QualityBreakdown;
  /** 内容长度 */
  contentLength: number;
  /** 字数（中文 + 英文词） */
  wordCount: number;
  /** 链接数 */
  linkCount: number;
  /** 是否可能过时（超过 180 天） */
  isStale: boolean;
  /** 天数（距今天） */
  ageDays: number;
  /** 读取失败标记 */
  readError?: boolean;
}

/** 贡献者统计项 */
interface ContributorStat {
  /** 贡献者（顶层目录名） */
  name: string;
  /** 笔记数 */
  count: number;
  /** 平均质量分 */
  avgScore: number;
  /** 高质量数 */
  highCount: number;
  /** 低质量数 */
  lowCount: number;
}

// ============================================
// 常量
// ============================================

/** 质量等级配置 */
const QUALITY_META: Record<QualityLevel, { label: string; color: string; range: string }> = {
  high: { label: "高质量", color: "#10B981", range: "≥ 70" },
  medium: { label: "中质量", color: "#F59E0B", range: "40-69" },
  low: { label: "低质量", color: "#EF4444", range: "< 40" },
};

/** 过期阈值（天） */
const STALE_THRESHOLD_DAYS = 180;
/** 抽样上限（避免请求过多） */
const SAMPLE_LIMIT = 50;
/** 批量读取并发数 */
const READ_BATCH_SIZE = 5;

// ============================================
// 主组件
// ============================================

export default function KnowledgeQualityPanel() {
  // 全部笔记列表
  const [notes, setNotes] = useState<NoteMeta[]>([]);
  // 质量评估结果
  const [qualityItems, setQualityItems] = useState<QualityItem[]>([]);
  // 加载状态
  const [loading, setLoading] = useState(true);
  // 进度提示
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  // 选中的笔记详情
  const [selectedNote, setSelectedNote] = useState<{
    title: string;
    path: string;
    content: string;
    loading: boolean;
    quality?: QualityItem;
  } | null>(null);
  // v6.23 质量统计关联查看：当前展开的等级（high/medium/low），null 表示未展开
  const [expandedLevel, setExpandedLevel] = useState<QualityLevel | null>(null);

  // ============================================
  // 递归扫描 Vault 获取全部 .md 文件
  // ============================================
  const scanVault = useCallback(async (): Promise<NoteMeta[]> => {
    const result: NoteMeta[] = [];
    const visited = new Set<string>();
    const queue: Array<{ dir: string; depth: number }> = [{ dir: ".", depth: 0 }];
    const MAX_DEPTH = 6;
    const MAX_FILES = 2000;

    while (queue.length > 0 && result.length < MAX_FILES) {
      const { dir, depth } = queue.shift()!;
      if (visited.has(dir)) continue;
      visited.add(dir);

      try {
        const url = `/vault-api/list?dir=${encodeURIComponent(dir)}&ext=`;
        const resp = await fetch(url);
        if (!resp.ok) continue;
        const json = await resp.json();
        if (!json?.success || !Array.isArray(json.files)) continue;

        for (const f of json.files) {
          if (result.length >= MAX_FILES) break;
          if (f.isDirectory) {
            if (depth < MAX_DEPTH) {
              queue.push({ dir: f.path, depth: depth + 1 });
            }
          } else if (f.name && f.name.endsWith(".md")) {
            result.push({
              name: f.name,
              path: f.path,
              mtime: f.mtime || new Date().toISOString(),
              size: f.size || 0,
            });
          }
        }
      } catch {
        // 单个目录扫描失败，跳过
      }
    }
    return result;
  }, []);

  // ============================================
  // 质量评分算法
  // ============================================
  const computeQuality = useCallback((content: string): QualityBreakdown => {
    const length = content.length;

    // 长度分（0-30）
    let lengthScore = 0;
    if (length >= 1500) lengthScore = 30;
    else if (length >= 500) lengthScore = 25;
    else if (length >= 200) lengthScore = 18;
    else if (length >= 100) lengthScore = 10;
    else if (length > 0) lengthScore = 5;

    // 结构分（0-25）：基于 Markdown 结构元素
    let structureScore = 0;
    if ( /^#/m.test(content)) structureScore += 6; // 有标题
    if (/^#{2,3}/m.test(content)) structureScore += 4; // 有二级以上标题
    if (/^\s*[-*+]\s/m.test(content)) structureScore += 5; // 有列表
    if (/```/.test(content)) structureScore += 5; // 有代码块
    if (/\|.*\|.*\n\|[-:| ]+\|/.test(content)) structureScore += 5; // 有表格
    structureScore = Math.min(structureScore, 25);

    // 链接分（0-20）
    const wikiLinks = (content.match(/\[\[([^\]]+)\]\]/g) || []).length;
    const mdLinks = (content.match(/\[([^\]]+)\]\([^)]+\)/g) || []).length;
    const totalLinks = wikiLinks + mdLinks;
    let linkScore = 0;
    if (totalLinks >= 4) linkScore = 20;
    else if (totalLinks >= 1) linkScore = 10;

    // 字数分（0-25）：中文按字符计，英文按词计
    const chineseChars = (content.match(/[\u4e00-\u9fa5]/g) || []).length;
    const englishWords = (content.replace(/[\u4e00-\u9fa5]/g, " ").match(/[a-zA-Z]+/g) || []).length;
    const wordCount = chineseChars + englishWords;
    let wordCountScore = 0;
    if (wordCount >= 200) wordCountScore = 25;
    else if (wordCount >= 50) wordCountScore = 15;
    else if (wordCount > 0) wordCountScore = 5;

    const total = Math.min(lengthScore + structureScore + linkScore + wordCountScore, 100);
    return { lengthScore, structureScore, linkScore, wordCountScore, total };
  }, []);

  // ============================================
  // 读取并评估笔记质量（分批并发）
  // ============================================
  const evaluateNotes = useCallback(
    async (noteList: NoteMeta[]): Promise<QualityItem[]> => {
      // 抽样：优先评估最近修改的笔记（避免评估过多旧笔记）
      const sorted = [...noteList].sort(
        (a, b) => new Date(b.mtime).getTime() - new Date(a.mtime).getTime()
      );
      const sample = sorted.slice(0, SAMPLE_LIMIT);
      const results: QualityItem[] = [];
      setProgress({ done: 0, total: sample.length });

      // 分批读取
      for (let i = 0; i < sample.length; i += READ_BATCH_SIZE) {
        const batch = sample.slice(i, i + READ_BATCH_SIZE);
        const contents = await Promise.all(
          batch.map(async (note) => {
            try {
              const resp = await fetch(`/vault-api/read?path=${encodeURIComponent(note.path)}`);
              if (!resp.ok) return { note, content: "", error: true as const };
              const json = await resp.json();
              if (!json?.success) return { note, content: "", error: true as const };
              return { note, content: json.content || "", error: false as const };
            } catch {
              return { note, content: "", error: true as const };
            }
          })
        );

        for (const { note, content, error } of contents) {
          const breakdown = error ? {
            lengthScore: 0, structureScore: 0, linkScore: 0, wordCountScore: 0, total: 0,
          } : computeQuality(content);

          const ageDays = Math.max(
            0,
            (Date.now() - new Date(note.mtime).getTime()) / (24 * 60 * 60 * 1000)
          );
          const isStale = ageDays > STALE_THRESHOLD_DAYS;
          const level: QualityLevel =
            breakdown.total >= 70 ? "high" : breakdown.total >= 40 ? "medium" : "low";

          const chineseChars = error ? 0 : (content.match(/[\u4e00-\u9fa5]/g) || []).length;
          const englishWords = error ? 0 : (content.replace(/[\u4e00-\u9fa5]/g, " ").match(/[a-zA-Z]+/g) || []).length;
          const wikiLinks = error ? 0 : (content.match(/\[\[([^\]]+)\]\]/g) || []).length;
          const mdLinks = error ? 0 : (content.match(/\[([^\]]+)\]\([^)]+\)/g) || []).length;

          results.push({
            note,
            score: breakdown.total,
            level,
            breakdown,
            contentLength: content.length,
            wordCount: chineseChars + englishWords,
            linkCount: wikiLinks + mdLinks,
            isStale,
            ageDays: Math.round(ageDays),
            readError: error || undefined,
          });
        }

        setProgress({ done: Math.min(i + READ_BATCH_SIZE, sample.length), total: sample.length });
      }

      return results;
    },
    [computeQuality]
  );

  // ============================================
  // 加载并评估
  // ============================================
  const loadAndEvaluate = useCallback(async () => {
    setLoading(true);
    setProgress(null);
    try {
      const scanned = await scanVault();
      setNotes(scanned);
      const evaluated = await evaluateNotes(scanned);
      setQualityItems(evaluated);
    } catch (err) {
      console.warn("[KnowledgeQualityPanel] 加载失败:", err);
      setNotes([]);
      setQualityItems([]);
    } finally {
      setLoading(false);
      setProgress(null);
    }
  }, [scanVault, evaluateNotes]);

  useEffect(() => {
    void loadAndEvaluate();
  }, [loadAndEvaluate]);

  // ============================================
  // 质量分布统计
  // ============================================
  const distribution = useMemo(() => {
    const high = qualityItems.filter((q) => q.level === "high").length;
    const medium = qualityItems.filter((q) => q.level === "medium").length;
    const low = qualityItems.filter((q) => q.level === "low").length;
    return { high, medium, low, total: qualityItems.length };
  }, [qualityItems]);

  // ============================================
  // 饼图数据
  // ============================================
  const pieData = useMemo(() => {
    return [
      { name: "高质量", value: distribution.high, color: QUALITY_META.high.color },
      { name: "中质量", value: distribution.medium, color: QUALITY_META.medium.color },
      { name: "低质量", value: distribution.low, color: QUALITY_META.low.color },
    ].filter((d) => d.value > 0);
  }, [distribution]);

  // ============================================
  // 低质量知识列表（按质量分升序，优先处理）
  // ============================================
  const lowQualityList = useMemo(() => {
    return qualityItems
      .filter((q) => q.level === "low")
      .sort((a, b) => a.score - b.score)
      .slice(0, 15);
  }, [qualityItems]);

  // v6.23 按质量等级过滤的文章列表（统计卡片「查看」展开用）
  const levelList = useMemo(() => {
    if (!expandedLevel) return [];
    const sorted = qualityItems
      .filter((q) => q.level === expandedLevel)
      .slice()
      .sort((a, b) => b.score - a.score);
    return sorted.slice(0, 50);
  }, [qualityItems, expandedLevel]);

  // ============================================
  // 过时知识列表
  // ============================================
  const staleList = useMemo(() => {
    return qualityItems
      .filter((q) => q.isStale)
      .sort((a, b) => b.ageDays - a.ageDays)
      .slice(0, 10);
  }, [qualityItems]);

  // ============================================
  // 贡献者排行榜（基于路径顶层目录解析）
  // ============================================
  const contributors = useMemo((): ContributorStat[] => {
    const map = new Map<string, QualityItem[]>();
    for (const item of qualityItems) {
      const topDir = item.note.path.split("/")[0] || "未分类";
      if (!map.has(topDir)) map.set(topDir, []);
      map.get(topDir)!.push(item);
    }
    const result: ContributorStat[] = [];
    for (const [name, items] of map) {
      const avgScore = items.length > 0
        ? Math.round(items.reduce((s, i) => s + i.score, 0) / items.length)
        : 0;
      result.push({
        name,
        count: items.length,
        avgScore,
        highCount: items.filter((i) => i.level === "high").length,
        lowCount: items.filter((i) => i.level === "low").length,
      });
    }
    // 按笔记数降序
    return result.sort((a, b) => b.count - a.count).slice(0, 10);
  }, [qualityItems]);

  // ============================================
  // 贡献者柱状图数据
  // ============================================
  const contributorChartData = useMemo(() => {
    return contributors.map((c) => ({
      name: c.name.length > 12 ? c.name.slice(0, 10) + "..." : c.name,
      fullName: c.name,
      笔记数: c.count,
      平均分: c.avgScore,
    }));
  }, [contributors]);

  // ============================================
  // 整体统计
  // ============================================
  const overallStats = useMemo(() => {
    const total = qualityItems.length;
    const avgScore = total > 0
      ? Math.round(qualityItems.reduce((s, q) => s + q.score, 0) / total)
      : 0;
    const staleCount = qualityItems.filter((q) => q.isStale).length;
    const errorCount = qualityItems.filter((q) => q.readError).length;
    return {
      total,
      avgScore,
      staleCount,
      errorCount,
      scannedVault: notes.length,
      sampled: qualityItems.length,
    };
  }, [qualityItems, notes]);

  // ============================================
  // 打开笔记详情
  // ============================================
  const openNote = useCallback(async (item: QualityItem) => {
    setSelectedNote({
      title: item.note.name.replace(/\.md$/i, ""),
      path: item.note.path,
      content: "",
      loading: true,
      quality: item,
    });
    try {
      const resp = await fetch(`/vault-api/read?path=${encodeURIComponent(item.note.path)}`);
      if (resp.ok) {
        const json = await resp.json();
        if (json?.success && json.content) {
          setSelectedNote({
            title: item.note.name.replace(/\.md$/i, ""),
            path: item.note.path,
            content: json.content,
            loading: false,
            quality: item,
          });
          return;
        }
      }
      setSelectedNote({
        title: item.note.name.replace(/\.md$/i, ""),
        path: item.note.path,
        content: "读取失败：文件不存在或无访问权限",
        loading: false,
        quality: item,
      });
    } catch (err) {
      setSelectedNote({
        title: item.note.name.replace(/\.md$/i, ""),
        path: item.note.path,
        content: `读取失败：${(err as Error).message}`,
        loading: false,
        quality: item,
      });
    }
  }, []);

  // ============================================
  // 渲染
  // ============================================
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="relative h-full flex flex-col rounded-xl border border-white/[0.06] bg-gradient-to-br from-[#131826]/60 to-[#0a0e1a]/60 backdrop-blur-md overflow-hidden"
    >
      {/* 顶部光带 */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-blue-500/50 to-transparent" />

      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-white/[0.04]">
        <div className="flex items-center gap-2.5">
          <div className="w-7 h-7 rounded-md flex items-center justify-center bg-blue-500/15 border border-blue-500/30">
            <BarChart3 size={13} className="text-blue-400" />
          </div>
          <div>
            <h3 className="text-[13px] font-semibold text-white tracking-tight">知识质量评估</h3>
            <p className="text-[9px] font-mono text-gray-500 uppercase tracking-wider mt-0.5">
              Quality Panel · 抽样 {overallStats.sampled}/{overallStats.scannedVault} · 平均分 {overallStats.avgScore} · 过时 {overallStats.staleCount}
            </p>
          </div>
        </div>

        <button
          onClick={loadAndEvaluate}
          disabled={loading}
          className="flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-blue-500/15 border border-blue-500/30 hover:bg-blue-500/25 transition-colors disabled:opacity-50"
        >
          {loading ? (
            <Loader2 size={11} className="text-blue-400 animate-spin" />
          ) : (
            <RefreshCw size={11} className="text-blue-400" />
          )}
          <span className="text-[9px] font-mono text-blue-300 uppercase tracking-wider">
            {loading ? "评估中..." : "重新评估"}
          </span>
        </button>
      </div>

      {/* 进度提示 */}
      {progress && (
        <div className="px-4 py-1.5 border-b border-white/[0.04] bg-blue-500/[0.03]">
          <div className="flex items-center gap-2">
            <Loader2 size={10} className="text-blue-400 animate-spin" />
            <span className="text-[10px] font-mono text-blue-300">
              正在抽样读取并评估质量：{progress.done} / {progress.total}
            </span>
            <div className="flex-1 h-1 bg-white/[0.04] rounded-full overflow-hidden">
              <div
                className="h-full bg-blue-500/60 transition-all"
                style={{ width: `${progress.total > 0 ? (progress.done / progress.total) * 100 : 0}%` }}
              />
            </div>
          </div>
        </div>
      )}

      {/* 内容区 */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {loading && qualityItems.length === 0 ? (
          <div className="h-full flex flex-col items-center justify-center text-gray-500">
            <Loader2 size={20} className="animate-spin text-blue-400 mb-2" />
            <span className="text-[11px] font-mono">正在扫描并评估知识质量...</span>
          </div>
        ) : qualityItems.length === 0 ? (
          <div className="h-full flex flex-col items-center justify-center text-center py-12">
            <BarChart3 size={28} className="text-gray-700 mb-2" />
            <p className="text-[11px] font-mono text-gray-500">
              暂无质量评估数据，知识库暂未接入
            </p>
          </div>
        ) : (
          <>
            {/* 第一行：质量分布饼图 + 质量分布统计 */}
            <div className="grid grid-cols-2 gap-3">
              {/* 质量分布饼图 */}
              <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-3">
                <div className="flex items-center gap-1.5 mb-2">
                  <BarChart3 size={11} className="text-blue-400" />
                  <span className="text-[10px] font-mono text-gray-400 uppercase tracking-wider">
                    质量分布
                  </span>
                </div>
                {pieData.length === 0 ? (
                  <div className="h-[180px] flex items-center justify-center text-[10px] font-mono text-gray-600">
                    暂无数据
                  </div>
                ) : (
                  <div className="h-[180px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={pieData}
                          cx="50%"
                          cy="50%"
                          innerRadius={45}
                          outerRadius={70}
                          paddingAngle={2}
                          dataKey="value"
                          nameKey="name"
                        >
                          {pieData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip
                          contentStyle={{
                            background: "rgba(10,14,26,0.95)",
                            border: "1px solid rgba(59,130,246,0.3)",
                            borderRadius: "6px",
                            fontSize: "11px",
                            fontFamily: "monospace",
                          }}
                        />
                        <Legend
                          wrapperStyle={{ fontSize: "10px", fontFamily: "monospace" }}
                          iconType="circle"
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </div>

              {/* 质量分布统计卡片 */}
              <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-3">
                <div className="flex items-center gap-1.5 mb-2">
                  <Award size={11} className="text-blue-400" />
                  <span className="text-[10px] font-mono text-gray-400 uppercase tracking-wider">
                    质量统计
                  </span>
                  {expandedLevel && (
                    <button
                      onClick={() => setExpandedLevel(null)}
                      className="ml-auto flex items-center gap-0.5 text-[9px] font-mono text-gray-500 hover:text-gray-300 transition-colors"
                    >
                      <X size={9} /> 收起
                    </button>
                  )}
                </div>
                <div className="space-y-2">
                  {(Object.keys(QUALITY_META) as QualityLevel[]).map((level) => {
                    const meta = QUALITY_META[level];
                    const count = distribution[level];
                    const percent = distribution.total > 0
                      ? Math.round((count / distribution.total) * 100)
                      : 0;
                    const isExpanded = expandedLevel === level;
                    return (
                      <div
                        key={level}
                        className={`rounded-md border p-2 transition-colors ${
                          isExpanded ? "ring-1" : ""
                        }`}
                        style={{
                          borderColor: isExpanded ? meta.color : `${meta.color}25`,
                          background: isExpanded ? `${meta.color}12` : `${meta.color}08`,
                          boxShadow: isExpanded ? `0 0 12px ${meta.color}30` : undefined,
                        }}
                      >
                        <div className="flex items-center justify-between mb-1">
                          <div className="flex items-center gap-1.5">
                            <span
                              className="w-2 h-2 rounded-full"
                              style={{ background: meta.color }}
                            />
                            <span className="text-[11px] font-mono text-gray-300">
                              {meta.label}
                            </span>
                            <span className="text-[8px] font-mono text-gray-600">
                              ({meta.range})
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span
                              className="text-[14px] font-mono font-bold"
                              style={{ color: meta.color }}
                            >
                              {count}
                            </span>
                            {/* v6.23 关联查看：点击展开该等级具体文章列表 */}
                            {count > 0 && (
                              <button
                                onClick={() =>
                                  setExpandedLevel(isExpanded ? null : level)
                                }
                                className="flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[9px] font-mono transition-colors"
                                style={{
                                  color: meta.color,
                                  border: `1px solid ${meta.color}40`,
                                  background: `${meta.color}0d`,
                                }}
                              >
                                {isExpanded ? (
                                  <ChevronUp size={9} />
                                ) : (
                                  <ChevronDown size={9} />
                                )}
                                查看文章
                              </button>
                            )}
                          </div>
                        </div>
                        {/* 进度条 */}
                        <div className="h-1 bg-white/[0.04] rounded-full overflow-hidden">
                          <div
                            className="h-full rounded-full transition-all"
                            style={{ width: `${percent}%`, background: meta.color }}
                          />
                        </div>
                        <span className="text-[8px] font-mono text-gray-600 mt-0.5 inline-block">
                          占比 {percent}%
                        </span>

                        {/* v6.23 展开：该等级具体文章列表（可点开查看详情） */}
                        {isExpanded && (
                          <div className="mt-2 pt-2 border-t border-white/[0.06] space-y-1 max-h-[240px] overflow-y-auto">
                            {levelList.length === 0 ? (
                              <div className="text-[9px] font-mono text-gray-600 py-1 text-center">
                                暂无该等级文章
                              </div>
                            ) : (
                              levelList.map((item, idx) => (
                                <button
                                  key={`${expandedLevel}-${item.note.path}-${idx}`}
                                  onClick={() => openNote(item)}
                                  className="w-full text-left px-2 py-1.5 rounded-md border border-white/[0.04] bg-white/[0.01] hover:bg-white/[0.05] transition-colors group"
                                >
                                  <div className="flex items-center gap-2">
                                    <span
                                      className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                                      style={{ background: meta.color }}
                                    />
                                    <span className="text-[10px] text-gray-300 truncate flex-1 group-hover:text-white">
                                      {item.note.name.replace(/\.md$/i, "")}
                                    </span>
                                    <span
                                      className="text-[8px] font-mono flex-shrink-0"
                                      style={{ color: meta.color }}
                                    >
                                      {item.score}分
                                    </span>
                                    <ExternalLink
                                      size={9}
                                      className="text-gray-700 group-hover:text-gray-400 flex-shrink-0"
                                    />
                                  </div>
                                  <div className="text-[8px] font-mono text-gray-600 mt-0.5 pl-3.5 truncate">
                                    {item.note.path}
                                  </div>
                                </button>
                              ))
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* 第二行：贡献者排行榜柱状图 */}
            <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-3">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-1.5">
                  <Users size={11} className="text-blue-400" />
                  <span className="text-[10px] font-mono text-gray-400 uppercase tracking-wider">
                    知识贡献者排行（按目录解析）
                  </span>
                </div>
                <span className="text-[9px] font-mono text-gray-600">
                  共 {contributors.length} 个分类
                </span>
              </div>
              {contributorChartData.length === 0 ? (
                <div className="h-[120px] flex items-center justify-center text-[10px] font-mono text-gray-600">
                  暂无数据
                </div>
              ) : (
                <div className="h-[160px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={contributorChartData} margin={{ top: 5, right: 5, bottom: 5, left: 5 }}>
                      <XAxis
                        dataKey="name"
                        tick={{ fill: "#6B7280", fontSize: 8, fontFamily: "monospace" }}
                        interval={0}
                        angle={-20}
                        textAnchor="end"
                        height={40}
                      />
                      <YAxis tick={{ fill: "#6B7280", fontSize: 9, fontFamily: "monospace" }} />
                      <Tooltip
                        contentStyle={{
                          background: "rgba(10,14,26,0.95)",
                          border: "1px solid rgba(59,130,246,0.3)",
                          borderRadius: "6px",
                          fontSize: "11px",
                          fontFamily: "monospace",
                        }}
                        cursor={{ fill: "rgba(59,130,246,0.05)" }}
                      />
                      <Legend wrapperStyle={{ fontSize: "10px", fontFamily: "monospace" }} />
                      <Bar dataKey="笔记数" fill="#3B82F6" radius={[3, 3, 0, 0]} />
                      <Bar dataKey="平均分" fill="#10B981" radius={[3, 3, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>

            {/* 第三行：低质量知识列表（优先处理） */}
            <div className="rounded-lg border border-red-500/20 bg-red-500/[0.02] p-3">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-1.5">
                  <AlertTriangle size={11} className="text-red-400" />
                  <span className="text-[10px] font-mono text-red-300 uppercase tracking-wider">
                    低质量知识（优先处理）
                  </span>
                </div>
                <span className="text-[9px] font-mono text-gray-600">
                  {lowQualityList.length} 条 · 按质量分升序
                </span>
              </div>
              {lowQualityList.length === 0 ? (
                <div className="text-[10px] font-mono text-gray-500 py-3 text-center">
                  ✅ 未发现低质量知识
                </div>
              ) : (
                <div className="space-y-1 max-h-[280px] overflow-y-auto">
                  {lowQualityList.map((item, idx) => (
                    <QualityItemRow
                      key={`${item.note.path}-${idx}`}
                      item={item}
                      rank={idx + 1}
                      onClick={() => openNote(item)}
                    />
                  ))}
                </div>
              )}
            </div>

            {/* 第四行：可能过时的知识 */}
            {staleList.length > 0 && (
              <div className="rounded-lg border border-amber-500/20 bg-amber-500/[0.02] p-3">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-1.5">
                    <Clock size={11} className="text-amber-400" />
                    <span className="text-[10px] font-mono text-amber-300 uppercase tracking-wider">
                      可能过时的知识（&gt; {STALE_THRESHOLD_DAYS} 天未更新）
                    </span>
                  </div>
                  <span className="text-[9px] font-mono text-gray-600">
                    {staleList.length} 条
                  </span>
                </div>
                <div className="space-y-1 max-h-[200px] overflow-y-auto">
                  {staleList.map((item, idx) => (
                    <button
                      key={`${item.note.path}-stale-${idx}`}
                      onClick={() => openNote(item)}
                      className="w-full text-left p-2 rounded-md border border-white/[0.04] bg-white/[0.01] hover:bg-amber-500/[0.04] hover:border-amber-500/30 transition-colors group"
                    >
                      <div className="flex items-center gap-2">
                        <Clock size={10} className="text-amber-400/70 flex-shrink-0" />
                        <span className="text-[11px] text-gray-300 truncate flex-1 group-hover:text-amber-300">
                          {item.note.name.replace(/\.md$/i, "")}
                        </span>
                        <span className="text-[9px] font-mono text-amber-400/80 flex-shrink-0">
                          {item.ageDays} 天前
                        </span>
                        <ExternalLink size={9} className="text-gray-700 group-hover:text-amber-400 flex-shrink-0" />
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* 底部数据来源 */}
      <div className="px-4 py-1.5 border-t border-white/[0.04]">
        <span className="text-[8px] font-mono text-gray-600">
          数据来源：/vault-api/list + /vault-api/read 抽样 · 评分算法：长度 30 + 结构 25 + 链接 20 + 字数 25
        </span>
      </div>

      {/* 笔记详情 Modal（含质量分项） */}
      <AnimatePresence>
        {selectedNote && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-8"
            onClick={() => setSelectedNote(null)}
          >
            <motion.div
              initial={{ scale: 0.95, y: 10 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.95, y: 10 }}
              transition={{ duration: 0.2 }}
              className="relative w-full max-w-3xl max-h-[85vh] flex flex-col rounded-xl border border-blue-500/30 bg-[#0a0e1a] overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between px-5 py-3 border-b border-white/[0.06]">
                <div className="flex items-center gap-2 min-w-0">
                  <FileText size={14} className="text-blue-400 flex-shrink-0" />
                  <h3 className="text-[14px] font-semibold text-white truncate">
                    {selectedNote.title}
                  </h3>
                  {selectedNote.quality && (
                    <span
                      className="flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[9px] font-mono border flex-shrink-0"
                      style={{
                        background: `${QUALITY_META[selectedNote.quality.level].color}15`,
                        color: QUALITY_META[selectedNote.quality.level].color,
                        borderColor: `${QUALITY_META[selectedNote.quality.level].color}30`,
                      }}
                    >
                      <Award size={8} />
                      {selectedNote.quality.score}/100
                    </span>
                  )}
                </div>
                <button
                  onClick={() => setSelectedNote(null)}
                  className="p-1 rounded-md text-gray-400 hover:text-white hover:bg-white/[0.05] transition-colors flex-shrink-0"
                >
                  <X size={16} />
                </button>
              </div>

              {/* 质量分项评分 */}
              {selectedNote.quality && (
                <div className="px-5 py-2 border-b border-white/[0.04] bg-white/[0.01]">
                  <div className="grid grid-cols-4 gap-2">
                    <ScoreBreakdown
                      icon={Type}
                      label="长度分"
                      score={selectedNote.quality.breakdown.lengthScore}
                      max={30}
                      color="#3B82F6"
                      detail={`${selectedNote.quality.contentLength} 字符`}
                    />
                    <ScoreBreakdown
                      icon={ListTree}
                      label="结构分"
                      score={selectedNote.quality.breakdown.structureScore}
                      max={25}
                      color="#A78BFA"
                      detail="标题/列表/代码/表格"
                    />
                    <ScoreBreakdown
                      icon={Link2}
                      label="链接分"
                      score={selectedNote.quality.breakdown.linkScore}
                      max={20}
                      color="#34D399"
                      detail={`${selectedNote.quality.linkCount} 个链接`}
                    />
                    <ScoreBreakdown
                      icon={Type}
                      label="字数分"
                      score={selectedNote.quality.breakdown.wordCountScore}
                      max={25}
                      color="#FBBF24"
                      detail={`${selectedNote.quality.wordCount} 字`}
                    />
                  </div>
                  {selectedNote.quality.isStale && (
                    <div className="mt-2 flex items-center gap-1 px-2 py-1 rounded bg-amber-500/10 border border-amber-500/20">
                      <Clock size={10} className="text-amber-400" />
                      <span className="text-[10px] font-mono text-amber-300">
                        ⚠ 可能过时：{selectedNote.quality.ageDays} 天未更新（&gt; {STALE_THRESHOLD_DAYS} 天阈值）
                      </span>
                    </div>
                  )}
                </div>
              )}

              <div className="flex-1 min-h-0 overflow-y-auto p-5">
                {selectedNote.loading ? (
                  <div className="flex items-center justify-center py-12 text-gray-500">
                    <Loader2 size={16} className="animate-spin mr-2" />
                    <span className="text-[13px] font-mono">正在从 Vault 加载...</span>
                  </div>
                ) : (
                  <pre className="text-[13px] text-gray-300 whitespace-pre-wrap break-words font-mono leading-relaxed">
                    {selectedNote.content || "暂无内容"}
                  </pre>
                )}
              </div>
              <div className="flex items-center justify-between px-5 py-2 border-t border-white/[0.06] bg-white/[0.01]">
                <span className="text-[10px] font-mono text-gray-600 truncate" title={selectedNote.path}>
                  📁 {selectedNote.path}
                </span>
                <span className="text-[10px] font-mono text-gray-600">数据来源：Obsidian Vault</span>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

// ============================================
// 子组件：质量评估项行
// ============================================

interface QualityItemRowProps {
  item: QualityItem;
  rank: number;
  onClick: () => void;
}

function QualityItemRow({ item, rank, onClick }: QualityItemRowProps) {
  const meta = QUALITY_META[item.level];
  const title = item.note.name.replace(/\.md$/i, "");
  const category = item.note.path.split("/")[0] || "未分类";

  return (
    <button
      onClick={onClick}
      className="w-full text-left p-2 rounded-md border border-white/[0.04] bg-white/[0.01] hover:bg-red-500/[0.04] hover:border-red-500/30 transition-colors group"
    >
      <div className="flex items-center gap-2">
        {/* 质量分 */}
        <div
          className="flex-shrink-0 w-9 h-9 rounded flex flex-col items-center justify-center"
          style={{ background: `${meta.color}10`, border: `1px solid ${meta.color}30` }}
        >
          <span className="text-[11px] font-mono font-bold" style={{ color: meta.color }}>
            {item.score}
          </span>
          <span className="text-[7px] font-mono text-gray-600">分</span>
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 mb-0.5">
            <span className="text-[8px] font-mono text-gray-600">#{rank}</span>
            <span className="text-[11px] text-gray-200 truncate group-hover:text-red-300">
              {title}
            </span>
            {item.isStale && (
              <span className="flex items-center gap-0.5 px-1 py-0.5 rounded text-[7px] font-mono bg-amber-500/15 text-amber-400">
                <Clock size={7} />
                {item.ageDays}d
              </span>
            )}
            {item.readError && (
              <span className="flex items-center gap-0.5 px-1 py-0.5 rounded text-[7px] font-mono bg-red-500/15 text-red-400">
                <AlertTriangle size={7} />
                读取失败
              </span>
            )}
          </div>
          <div className="flex items-center gap-2 text-[8px] font-mono text-gray-600">
            <span>{category}</span>
            <span>·</span>
            <span>{item.contentLength} 字符</span>
            <span>·</span>
            <span>{item.wordCount} 字</span>
            <span>·</span>
            <span>{item.linkCount} 链接</span>
          </div>
        </div>

        <ExternalLink size={9} className="text-gray-700 group-hover:text-red-400 flex-shrink-0" />
      </div>
    </button>
  );
}

// ============================================
// 子组件：分项评分卡
// ============================================

interface ScoreBreakdownProps {
  icon: typeof Type;
  label: string;
  score: number;
  max: number;
  color: string;
  detail: string;
}

function ScoreBreakdown({ icon: Icon, label, score, max, color, detail }: ScoreBreakdownProps) {
  const percent = max > 0 ? (score / max) * 100 : 0;
  return (
    <div className="rounded-md border border-white/[0.04] bg-white/[0.01] p-2">
      <div className="flex items-center gap-1 mb-1">
        <Icon size={9} style={{ color }} />
        <span className="text-[9px] font-mono text-gray-500 uppercase tracking-wider">
          {label}
        </span>
      </div>
      <div className="flex items-baseline gap-0.5 mb-1">
        <span className="text-[14px] font-mono font-bold" style={{ color }}>
          {score}
        </span>
        <span className="text-[9px] font-mono text-gray-600">/{max}</span>
      </div>
      {/* 进度条 */}
      <div className="h-0.5 bg-white/[0.04] rounded-full overflow-hidden mb-1">
        <div
          className="h-full rounded-full transition-all"
          style={{ width: `${percent}%`, background: color }}
        />
      </div>
      <span className="text-[8px] font-mono text-gray-600">{detail}</span>
    </div>
  );
}
